import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import AST from '@highcharts/dashboards/es-modules/Core/Renderer/HTML/AST.js';
import { DashboardWidget } from '@h20-shared/dashboards';
import { DASHBOARD_WIDGET_MODE } from '../dashboard-widget-mode';
import type { HtmlWidgetConfig } from './dashboard-html.schema';

/** Shows authored HTML in a dashboard cell, in the reader's language. */
@Component({
    selector: 'pulse-dashboard-html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule],
    templateUrl: './dashboard-html.widget.html',
    styleUrl: './dashboard-html.widget.scss',
    // Angular's component styles skip HTML added at runtime, so style it globally
    encapsulation: ViewEncapsulation.None,
})
export class DashboardHtmlWidget extends DashboardWidget<never, HtmlWidgetConfig> {
    private readonly translate = inject(TranslateService);
    private readonly langChange = toSignal(this.translate.onLangChange);
    private readonly container = viewChild.required<ElementRef<HTMLElement>>('container');

    protected readonly authoring = inject(DASHBOARD_WIDGET_MODE) === 'authoring';

    /** The HTML for the reader's language when it is switched on and filled in, otherwise the default HTML. */
    protected readonly html = computed(() => {
        this.langChange();
        const config = this.config();
        const readerLanguage = config?.languages[this.translate.currentLang];
        return (readerLanguage?.enabled === true && readerLanguage.html) || config?.html || '';
    });

    constructor() {
        super();
        // Highcharts' filter strips scripts but keeps inline styles.
        afterRenderEffect(() => AST.setElementHTML(this.container().nativeElement, this.html()));
    }
}
